import {
  all,
  call,
  put,
  select,
  takeLatest,
  take,
  fork,
} from 'redux-saga/effects';
import uiDux from 'src/redux/ui';
import appDux from 'src/redux/app/';
import undoDux from 'src/redux/undo';
import macheteDux from 'src/redux/machete';
import keyboardEventsDux from 'src/redux/keyboardEvents';
import splitDux from 'src/redux/split';
import measureModeDux from 'src/redux/measureMode';
import { actions as macheteActions } from 'src/machete-redux';
import constants from 'src/constants';
import keyboardEventChannel from './keyboardEventChannel';

const { MODE_NAMES } = constants.app;

const KEY_HANDLER_MAP = {
  Enter: handleEnterKeySaga,
  Escape: handleEscapeKeySaga,
  m: handleMKeySaga,
  M: handleMKeySaga,
  z: handleUndoRedoKeysSaga,
  Z: handleUndoRedoKeysSaga,
};

const {
  isDeletingSplit,
  isSplitConfirmDisabled,
  isSplitToolActive,
} = splitDux.selectors;

const {
  handleCancelDeleteSplit,
  handleSplitToolCancelSplit,
  handleConfirmDeleteSplit,
  handleSplitToolConfirmSplit,
} = splitDux.actions;

export function* handleUndoRedoKeysSaga({ type, metaKey, shiftKey }) {
  const shouldAllowUpdateSelection = yield select(
    macheteDux.selectors.shouldAllowUpdateSelection,
  );

  const { shouldRenderUndoRedoButtons } = uiDux.selectors;
  const shouldAllowUndoRedo = yield select(shouldRenderUndoRedoButtons);

  const canUndo = yield select(undoDux.selectors.isUndoEnabled);
  const canRedo = yield select(undoDux.selectors.isRedoEnabled);

  if (!(shouldAllowUpdateSelection && shouldAllowUndoRedo)) return null;

  if (type === 'keydown' && metaKey) {
    const { handleUndoClick, handleRedoClick } = undoDux.actions;
    const isUndoKeys = !shiftKey;
    const isRedoKeys = shiftKey;

    if (isUndoKeys && canUndo) {
      yield put(handleUndoClick());
    }

    if (isRedoKeys && canRedo) {
      yield put(handleRedoClick());
    }
  }
}

export function* handleEscapeKeySaga({ type }) {
  if (type === 'keyup') {
    const modeName = yield select(appDux.selectors.getActiveMode);

    if (modeName === MODE_NAMES.DESIGN) {
      const deletingSplit = yield select(isDeletingSplit);
      const splitToolActive = yield select(isSplitToolActive);

      if (deletingSplit) {
        yield put(handleCancelDeleteSplit());
      } else if (splitToolActive) {
        yield put(handleSplitToolCancelSplit());
      } else {
        yield put(macheteActions.segmentation.clearNodeSelection());
      }
    }
  }
}

export function* handleEnterKeySaga({ type }) {
  if (type === 'keyup') {
    const splitToolActive = yield select(isSplitToolActive);
    const deletingSplit = yield select(isDeletingSplit);
    const splitConfirmIsDisabled = yield select(isSplitConfirmDisabled);

    if (deletingSplit) {
      yield put(handleConfirmDeleteSplit());
    } else if (splitToolActive && !splitConfirmIsDisabled) {
      yield put(handleSplitToolConfirmSplit());
    }
  }
}

export function* handleMKeySaga({ type }) {
  if (type === 'keyup') {
    const isMeasureModeEnabled = yield select(
      measureModeDux.selectors.isMeasureModeEnabled,
    );

    if (isMeasureModeEnabled) {
      yield put(macheteDux.actions.toggleMeasureTool());
    }
  }
}

export function* keyboardEventsSaga() {
  const keyboardEventChan = yield call(keyboardEventChannel);

  while (true) {
    const keyboardEvent = yield take(keyboardEventChan);
    const handlerSaga = KEY_HANDLER_MAP[keyboardEvent.key];

    if (handlerSaga) {
      yield call(handlerSaga, keyboardEvent);
    }
  }
}

function* initializeKeyboardEventsSaga() {
  yield fork(keyboardEventsSaga);
}

export default function* sagas() {
  yield all([
    takeLatest(
      keyboardEventsDux.actions.initializeKeyboardEvents,
      initializeKeyboardEventsSaga,
    ),
  ]);
}
